import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Button, Grid, Tooltip, Typography } from "@mui/material";
import { mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import EasyEdit, { Types } from "react-easy-edit";
import EasyEditCustomTextbox from "../../../components/common/EasyEditCustomTextbox";
import FormGroupStatic from "../../../components/common/FormGroupStatic";
import { EasyEditCancelButton, EasyEditSaveButton } from "../../../components/common/EasyEditButtons";
import useAxios from "../../../hooks/useAxios";
import { useLoader } from "../../../components/context/LoaderContext";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../../components/context/SnackbarContext";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import { capitalize, DeleteDialog, DeleteDialogConfig, validateField } from "../../../utils/Utils";
import EasyEditCustomSelect from "components/common/EasyEditCustomSelect";

function CustomFieldDetails() {
    const { id } = useParams();
    const customFieldId = id
    const axiosInstance = useAxios();
    const navigate = useNavigate();
    const { showLoader, hideLoader } = useLoader();
    const [reloadPage, setReloadPage] = useState(false);
    const [customFieldData, setCustomFieldData] = useState();
    const { showSnackbar } = useSnackbar();
    const { preferredDateFormat, preTimeZoneKey } = useSelector((state) => state?.user);
    const userPermissions = useSelector((state) => state.user.permission);
    const [backendErrorMessages, setBackendErrorMessages] = useState({});
    const [customBackendErrorMessages, setCustomBackendErrorMessages] = useState({});
    const [errorMessages, setErrorMessages] = useState({});
    const [customFieldsDefaultData, setCustomFieldsDefaultData] = useState([]);

    useEffect(() => {
        async function fetchData() {
            showLoader();
            getData();
        }
        fetchData();
    }, [reloadPage]);


    const getData = async () => {
        await axiosInstance.get('api/custom-field/edit/' + customFieldId).then((response) => {
            if (response.status === 200) {
                setCustomFieldData(response?.data?.data?.record);
                setCustomFieldsDefaultData(response?.data?.data?.defaultData)
                hideLoader();
            }
        })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                        navigate("/settings/products");
                    }
                }
            });
    }

    async function deleteCustomFieldData(id) {
        const data = {
            id: id,
        }
        try {
            DeleteDialog.fire({
                ...DeleteDialogConfig({
                    confirm: async () => {
                        showLoader()
                        await axiosInstance.delete(`api/custom-field/destroy`, { data })
                            .then((response) => {
                                if (response.status === 200) {
                                    showSnackbar(response.data.message, "success");
                                    hideLoader();
                                    setReloadPage(reloadPage ? false : true);
                                    navigate("/settings/custom-fields");
                                }
                            })
                            .then((json) => { })
                            .catch((error) => {
                                if (error.response) {
                                    hideLoader();
                                    if (error.response.status === 400) {
                                        showSnackbar(error.response.data.message, "warning");
                                    }
                                }
                            });
                    },
                    cancel: () => {
                        hideLoader();
                    }
                })
            });
        } catch (error) {
            hideLoader();
            showSnackbar('Oops Something want wrong..!', "error");
        }
    }

    const handleSaveData = async (value, name, type = null) => {

        const formData = new FormData();
        formData.append('id', customFieldId)
        formData.append('model', customFieldData?.model || '')
        formData.append('field_name', customFieldData?.field_name || '')
        formData.append('display_type', customFieldData?.display_type || '')
        formData.append([name], value === 'N/A' ? '' : value)

        function getValueByKey(recordData, key) {
            return recordData[key];
        }
        const newValue = getValueByKey(customFieldData, name);

        if (newValue !== value && value !== 'N/A') {
            await axiosInstance.post("api/custom-field/update", formData).then(response => {
                if (response.status === 200) {
                    setReloadPage(reloadPage ? false : true)
                    showSnackbar(response.data.message, "success");
                    setAllowEditInline(allowEditInline ? false : true)
                    setErrorMessages({})
                    setBackendErrorMessages({})
                }
            }).then(json => {

            }).catch(error => {
                if (error.response) {
                    if (error.response.status === 422 || error.response.data.validation_errors) {
                        setAllowEditInline(true)
                        const errors = error.response.data.validation_errors;
                        Object.keys(errors).forEach(key => {
                            setBackendErrorMessages({ [key]: errors[key][0] });
                        });

                        const groupedErrors = {};
                        Object.entries(errors).forEach(([key, value]) => {
                            const baseKey = key.split('.')[0];

                            // Clean the error message:
                            let cleanedMessage = value[0]
                                .replace(/\.\d+/, '')                    // remove ".0", ".1", etc.
                                .replace(/_/g, ' ')                      // replace underscores with spaces
                                .replace(/([a-z])([A-Z])/g, '$1 $2')     // optional: split camelCase if needed
                                .trim();

                            if (!groupedErrors[baseKey]) {
                                groupedErrors[baseKey] = [];
                            }

                            groupedErrors[baseKey].push(cleanedMessage);
                        });
                        setCustomBackendErrorMessages(groupedErrors)
                    } else {
                        showSnackbar(error.response.data.message, 'error')
                    }
                }
            });
        } else {
            setReloadPage(reloadPage ? false : true)
            setAllowEditInline(allowEditInline ? false : true)
        }
    }

    const onCancelEdit = (fieldName) => {
        setErrorMessages({ [fieldName]: '' });
        setAllowEditInline(true);
    }

    const handleValidation = (value, fieldName, displayName, type, isrequired, min, max) => {
        const errorMsg = validateField(value, fieldName, displayName, type, isrequired, min, max);
        setErrorMessages(prevState => ({ ...prevState, [fieldName]: errorMsg }));
        return errorMsg === '';
    };

    const handleClick = (e) => {
        setAllowEditInline(false)
    }
    const [allowEditInline, setAllowEditInline] = React.useState(true);

    const formatFieldType = (type) => {
        return type
            .replace(/_/g, ' ')           // Replace underscores with spaces
            .replace(/[^a-zA-Z0-9 ]/g, '') // Remove any other special characters
            .replace(/\b\w/g, (c) => c.toUpperCase()); // Capitalize first letter of each word
    };

    const DISPLAY_MIN_MAX_LENGTH_OPTIONS = ['text', 'number', 'email'];
    const DISPLAY_ALLOW_MULTIPLE_OPTIONS = ['select', 'file'];


    return (
        <>
            <Helmet>
                <title> Custom Field Details | SolvCRM </title>
            </Helmet>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <div className="card p-16">
                        <Grid container alignItems={"center"} flexWrap={"nowrap"}>
                            <Grid item xs={'auto'} className="flex-shrink-1">
                                <Grid container alignItems={'flex-start'} spacing={1.5}>
                                    <Grid item xs>
                                        <Typography variant='title-medium-bold' component={'h6'}>{customFieldData?.field_name ? formatFieldType(customFieldData?.field_name) : 'N/A'}</Typography>
                                        <ul className='inline-details'>
                                            <li><span className="title">Last Updated:</span>
                                                {customFieldData?.updated_at
                                                    ? moment
                                                        .utc(customFieldData.updated_at)
                                                        .tz(preTimeZoneKey)
                                                        .format(preferredDateFormat)
                                                    : "N/A"}
                                            </li>
                                        </ul>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={'auto'} sx={{ marginLeft: 'auto' }}>
                                {userPermissions && userPermissions.includes("product_delete") &&
                                    <Tooltip title="Delete">
                                        <Button color="secondary" className="icon-only" size={'small'} onClick={() => { deleteCustomFieldData(customFieldId) }} sx={{ marginRight: 0.5 }}>
                                            <Icon path={mdiTrashCanOutline} color={'currentColor'} size={1} />
                                        </Button>
                                    </Tooltip>
                                }
                            </Grid>
                        </Grid>
                    </div>
                </Grid>

                <Grid item xs={12}>
                    <div className="card p-16 height-100p">
                        <Typography variant="title-medium-large-semibold" component={'h4'} className="mb-16">Custom Field Information</Typography>

                        <Grid container spacing={1.5}>
                            <Grid item xl={4} lg={4} xs={6}>
                                <EasyEdit
                                    type={Types.TEXT}
                                    onSave={(value) => { handleSaveData(value, 'model') }}
                                    onCancel={() => { onCancelEdit('model') }}
                                    value={customFieldData?.model ? formatFieldType(customFieldData?.model) : 'N/A'}
                                    allowEdit={false}
                                    editComponent={<EasyEditCustomTextbox focusCallback={e => { setAllowEditInline(false); }} label="Field Belongs To" value={customFieldData?.model} errorMessages={errorMessages['model']} />}
                                    displayComponent={<FormGroupStatic easyEdit label="Field Belongs To" value={customFieldData?.model || 'N/A'} />}
                                    onValidate={(value) => handleValidation(value, 'model', 'Field Belongs To', '', true)}
                                    validationMessage={errorMessages['model']}
                                    saveButtonLabel={<EasyEditSaveButton />}
                                    cancelButtonLabel={<EasyEditCancelButton />}
                                    onFocus={() => handleClick('')}
                                />
                                {backendErrorMessages['model'] && <span className="easy-edit-validation-error">{backendErrorMessages['model']}</span>}
                            </Grid>

                            <Grid item xl={4} lg={4} xs={6}>
                                <EasyEdit
                                    type={Types.TEXT}
                                    onSave={(value) => { handleSaveData(value, 'display_type') }}
                                    onCancel={() => { onCancelEdit('display_type') }}
                                    value={customFieldData?.display_type || 'N/A'}
                                    allowEdit={false}
                                    editComponent={<EasyEditCustomTextbox focusCallback={e => { setAllowEditInline(false); }} label="Field Type" value={customFieldData?.display_type} errorMessages={errorMessages['display_type']} />}
                                    displayComponent={<FormGroupStatic easyEdit label="Field Type" value={customFieldData?.display_type || 'N/A'} />}
                                    onValidate={(value) => handleValidation(value, 'display_type', 'Field Type', '', true)}
                                    validationMessage={errorMessages['display_type']}
                                    saveButtonLabel={<EasyEditSaveButton />}
                                    cancelButtonLabel={<EasyEditCancelButton />}
                                    onFocus={() => handleClick('')}
                                />

                                {backendErrorMessages['display_type'] && <span className="easy-edit-validation-error">{backendErrorMessages['display_type']}</span>}
                            </Grid>

                            <Grid item xl={4} lg={4} xs={6}>
                                <EasyEdit
                                    type={Types.TEXT}
                                    onSave={(value) => { handleSaveData(value, 'field_name') }}
                                    onCancel={() => { onCancelEdit('field_name') }}
                                    value={customFieldData?.field_name ? formatFieldType(customFieldData?.field_name) : 'N/A'}
                                    allowEdit={false}
                                    editComponent={<EasyEditCustomTextbox focusCallback={e => { setAllowEditInline(false); }} label="Field Name" value={customFieldData?.field_name} errorMessages={errorMessages['field_name']} />}
                                    displayComponent={<FormGroupStatic easyEdit label="Field Name" value={customFieldData?.field_name || 'N/A'} />}
                                    onValidate={(value) => handleValidation(value, 'field_name', 'Field Name', '', true)}
                                    validationMessage={errorMessages['field_name']}
                                    saveButtonLabel={<EasyEditSaveButton />}
                                    cancelButtonLabel={<EasyEditCancelButton />}
                                    onFocus={() => handleClick('')}
                                />

                                {backendErrorMessages['field_name'] && <span className="easy-edit-validation-error">{backendErrorMessages['field_name']}</span>}
                            </Grid>

                            <Grid item xl={4} lg={4} xs={6}>
                                <EasyEdit
                                    type={Types.TEXT}
                                    onSave={(value) => { handleSaveData(value, 'label') }}
                                    onCancel={() => { onCancelEdit('label') }}
                                    value={customFieldData?.label || 'N/A'}
                                    allowEdit={allowEditInline}
                                    editComponent={<EasyEditCustomTextbox focusCallback={e => { setAllowEditInline(false); }} label="Display Field Label" value={customFieldData?.label} errorMessages={errorMessages['label']} />}
                                    displayComponent={<FormGroupStatic easyEdit label="Display Field Label" value={customFieldData?.label || 'N/A'} />}
                                    onValidate={(value) => handleValidation(value, 'label', 'Display Field Label', '', true, 2, 50)}
                                    validationMessage={errorMessages['label']}
                                    saveButtonLabel={<EasyEditSaveButton />}
                                    cancelButtonLabel={<EasyEditCancelButton />}
                                    onFocus={() => handleClick('')}
                                />

                                {backendErrorMessages['label'] && <span className="easy-edit-validation-error">{backendErrorMessages['label']}</span>}
                            </Grid>



                            <Grid item xl={4} lg={4} xs={6}>
                                <EasyEdit
                                    type={Types.TEXT}
                                    onSave={(value) => { handleSaveData(value, 'placeholder_text') }}
                                    onCancel={() => { onCancelEdit('placeholder_text') }}
                                    value={customFieldData?.placeholder_text || 'N/A'}
                                    allowEdit={allowEditInline}
                                    editComponent={<EasyEditCustomTextbox focusCallback={e => { setAllowEditInline(false); }} label="Placeholder" value={customFieldData?.placeholder_text} errorMessages={errorMessages['placeholder_text']} />}
                                    displayComponent={<FormGroupStatic easyEdit label="Placeholder" value={customFieldData?.label || 'N/A'} />}
                                    onValidate={(value) => handleValidation(value, 'placeholder_text', 'Placeholder', '', false, 2, 50)}
                                    validationMessage={errorMessages['placeholder_text']}
                                    saveButtonLabel={<EasyEditSaveButton />}
                                    cancelButtonLabel={<EasyEditCancelButton />}
                                    onFocus={() => handleClick('')}
                                />

                                {backendErrorMessages['placeholder_text'] && <span className="easy-edit-validation-error">{backendErrorMessages['placeholder_text']}</span>}
                            </Grid>

                            {/* <Grid item xl={4} lg={4} xs={6}>
                                <EasyEdit
                                    type={Types.TEXT}
                                    onSave={(value) => { handleSaveData(value, 'field_size') }}
                                    onCancel={() => { onCancelEdit('field_size') }}
                                    value={customFieldData?.field_size || 'N/A'}
                                    allowEdit={allowEditInline}
                                    editComponent={<EasyEditCustomTextbox focusCallback={e => { setAllowEditInline(false); }} label="Field Size" value={customFieldData?.field_size} errorMessages={errorMessages['field_size']} />}
                                    displayComponent={<FormGroupStatic easyEdit label="Field Size" value={customFieldData?.field_size || 'N/A'} />}
                                    onValidate={(value) => handleValidation(value, 'field_size', 'Field Size', '', true, 0, 255)}
                                    validationMessage={errorMessages['field_size']}
                                    saveButtonLabel={<EasyEditSaveButton />}
                                    cancelButtonLabel={<EasyEditCancelButton />}
                                    onFocus={() => handleClick('')}
                                />

                                {backendErrorMessages['field_size'] && <span className="easy-edit-validation-error">{backendErrorMessages['field_size']}</span>}
                            </Grid> */}
                            {DISPLAY_MIN_MAX_LENGTH_OPTIONS.includes(customFieldData?.display_type) && (
                                <>
                                    <Grid item xl={4} lg={4} xs={6}>
                                        <EasyEdit
                                            type={Types.TEXT}
                                            onSave={(value) => { handleSaveData(value, 'max_input') }}
                                            onCancel={() => { onCancelEdit('max_input') }}
                                            value={customFieldData?.max_input || 'N/A'}
                                            allowEdit={allowEditInline}
                                            editComponent={<EasyEditCustomTextbox focusCallback={e => { setAllowEditInline(false); }} label="Field Maximum Input" value={customFieldData?.max_input} errorMessages={errorMessages['max_input']} />}
                                            displayComponent={<FormGroupStatic easyEdit label="Field Maximum Input" value={customFieldData?.max_input || 'N/A'} />}
                                            onValidate={(value) => handleValidation(value, 'max_input', 'Field Maximum Input', '', false, 0, 255)}
                                            validationMessage={errorMessages['max_input']}
                                            saveButtonLabel={<EasyEditSaveButton />}
                                            cancelButtonLabel={<EasyEditCancelButton />}
                                            onFocus={() => handleClick('')}
                                        />

                                        {backendErrorMessages['max_input'] && <span className="easy-edit-validation-error">{backendErrorMessages['max_input']}</span>}
                                    </Grid>

                                    <Grid item xl={4} lg={4} xs={6}>
                                        <EasyEdit
                                            type={Types.TEXT}
                                            onSave={(value) => { handleSaveData(value, 'min_input') }}
                                            onCancel={() => { onCancelEdit('min_input') }}
                                            value={customFieldData?.min_input || 'N/A'}
                                            allowEdit={allowEditInline}
                                            editComponent={<EasyEditCustomTextbox focusCallback={e => { setAllowEditInline(false); }} label="Field Minimum Input" value={customFieldData?.min_input} errorMessages={errorMessages['min_input']} />}
                                            displayComponent={<FormGroupStatic easyEdit label="Field Minimum Input" value={customFieldData?.min_input || 'N/A'} />}
                                            onValidate={(value) => handleValidation(value, 'min_input', 'Field Minimum Input', '', false, 0, 255)}
                                            validationMessage={errorMessages['min_input']}
                                            saveButtonLabel={<EasyEditSaveButton />}
                                            cancelButtonLabel={<EasyEditCancelButton />}
                                            onFocus={() => handleClick('')}
                                        />

                                        {backendErrorMessages['min_input'] && <span className="easy-edit-validation-error">{backendErrorMessages['min_input']}</span>}
                                    </Grid>
                                </>
                            )}
                            {DISPLAY_ALLOW_MULTIPLE_OPTIONS.includes(customFieldData?.display_type) && (
                                <Grid item xl={4} lg={4} xs={6}>
                                    <EasyEdit
                                        type={Types.SELECT}
                                        onSave={(value) => { handleSaveData(value, 'is_multiple') }}
                                        onCancel={() => { onCancelEdit() }}
                                        value={customFieldData?.is_multiple || 'N/A'}
                                        allowEdit={allowEditInline}
                                        editComponent={
                                            <EasyEditCustomSelect focusCallback={e => { setAllowEditInline(false); }} label="Multiple" fieldValue={customFieldData?.is_multiple || ''}
                                                items={[
                                                    { value: 'yes', option: 'Yes' },
                                                    { value: 'no', option: 'No' }
                                                ]}
                                            />
                                        }
                                        displayComponent={<FormGroupStatic easyEdit label="Multiple" value={customFieldData?.is_multiple || 'N/A'} />}
                                        saveButtonLabel={<EasyEditSaveButton />}
                                        cancelButtonLabel={<EasyEditCancelButton />}
                                        onFocus={() => handleClick('')}
                                    />
                                    {backendErrorMessages['is_multiple'] && <span className="easy-edit-validation-error">{backendErrorMessages['is_multiple']}</span>}
                                </Grid>
                            )}

                            <Grid item xl={4} lg={4} xs={6}>
                                <EasyEdit
                                    type={Types.SELECT}
                                    onSave={(value) => { handleSaveData(value, 'is_required') }}
                                    onCancel={() => { onCancelEdit() }}
                                    value={customFieldData?.is_required || 'N/A'}
                                    allowEdit={allowEditInline}
                                    editComponent={
                                        <EasyEditCustomSelect focusCallback={e => { setAllowEditInline(false); }} label="Required" fieldValue={customFieldData?.is_required || ''}
                                            items={[
                                                { value: 'yes', option: 'Yes' },
                                                { value: 'no', option: 'No' }
                                            ]}
                                        />
                                    }
                                    displayComponent={<FormGroupStatic easyEdit label="Required" value={customFieldData?.is_required || 'N/A'} />}
                                    saveButtonLabel={<EasyEditSaveButton />}
                                    cancelButtonLabel={<EasyEditCancelButton />}
                                    onFocus={() => handleClick('')}
                                />
                                {backendErrorMessages['is_required'] && <span className="easy-edit-validation-error">{backendErrorMessages['is_required']}</span>}
                            </Grid>

                            <Grid item xl={4} lg={4} xs={6}>
                                <EasyEdit
                                    type={Types.SELECT}
                                    onSave={(value) => { handleSaveData(value, 'collect_data_on_add') }}
                                    onCancel={() => { onCancelEdit() }}
                                    value={customFieldData?.collect_data_on_add || 'N/A'}
                                    allowEdit={allowEditInline}
                                    editComponent={
                                        <EasyEditCustomSelect focusCallback={e => { setAllowEditInline(false); }} label="Collect Data On Add" fieldValue={customFieldData?.collect_data_on_add || ''}
                                            items={[
                                                { value: 'yes', option: 'Yes' },
                                                { value: 'no', option: 'No' }
                                            ]}
                                        />
                                    }
                                    displayComponent={<FormGroupStatic easyEdit label="Collect Data On Add" value={customFieldData?.collect_data_on_add || 'N/A'} />}
                                    saveButtonLabel={<EasyEditSaveButton />}
                                    cancelButtonLabel={<EasyEditCancelButton />}
                                    onFocus={() => handleClick('')}
                                />
                                {backendErrorMessages['collect_data_on_add'] && <span className="easy-edit-validation-error">{backendErrorMessages['collect_data_on_add']}</span>}
                            </Grid>

                            <Grid item xl={4} lg={4} xs={6}>
                                <EasyEdit
                                    type={Types.SELECT}
                                    onSave={(value) => { handleSaveData(value, 'status') }}
                                    onCancel={() => { onCancelEdit() }}
                                    value={customFieldData?.status === 1 ? 'Active' : 'Inactive' || 'N/A'}
                                    allowEdit={allowEditInline}
                                    editComponent={
                                        <EasyEditCustomSelect focusCallback={e => { setAllowEditInline(false); }} label="Status" fieldValue={customFieldData?.status || ''}
                                            items={[
                                                { value: '1', option: 'Active' },
                                                { value: '0', option: 'Inactive' }
                                            ]}
                                        />
                                    }
                                    displayComponent={<FormGroupStatic easyEdit label="Status" value={customFieldData?.status === 1 ? 'Active' : 'Inactive' || 'N/A'} />}
                                    saveButtonLabel={<EasyEditSaveButton />}
                                    cancelButtonLabel={<EasyEditCancelButton />}
                                    onFocus={() => handleClick('')}
                                />
                                {backendErrorMessages['status'] && <span className="easy-edit-validation-error">{backendErrorMessages['status']}</span>}
                            </Grid>

                            <Grid item xl={4} lg={4} xs={6}>
                                <FormGroupStatic easyEdit label="Created by" value={customFieldData?.added_by?.name + ' (' + (customFieldData?.created_at
                                    ? moment
                                        .utc(customFieldData.created_at)
                                        .tz(preTimeZoneKey)
                                        .format(preferredDateFormat)
                                    : "N/A") + ')'} />
                            </Grid>

                            <Grid item xl={4} lg={4} xs={6}>
                                <FormGroupStatic easyEdit label="Modified by" value={customFieldData?.added_by?.name + ' (' + (customFieldData?.updated_at
                                    ? moment
                                        .utc(customFieldData.updated_at)
                                        .tz(preTimeZoneKey)
                                        .format(preferredDateFormat)
                                    : "N/A") + ')'} />
                            </Grid>
                        </Grid>
                    </div>
                </Grid>
            </Grid>

        </>
    );
}

export default CustomFieldDetails;